<div class="space-y-6">
  <div>
    <h2 class="mb-2 text-xl font-semibold text-gray-900">
      {{ 'contribute.reviewConfirm.title' | transloco }}
    </h2>
    <p class="text-gray-600">
      {{ 'contribute.reviewConfirm.description' | transloco }}
    </p>
  </div>

  <!-- Two Column Layout -->
  <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
    <!-- Left Column: Contribution Summary (Single Card with Sections) -->
    <div class="rounded-lg border border-greyscale-200 bg-white">
      <!-- Trip Section -->
      <div class="border-b border-greyscale-200 p-4">
        <h3 class="mb-3 font-semibold text-gray-900">{{ 'contribute.reviewConfirm.tripSummary' | transloco }}</h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.tripName' | transloco }}</span>
            <span class="font-medium text-right">{{ wizardService.trip()?.title }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.travelers' | transloco }}</span>
            <span class="font-medium">{{ wizardService.trip()?.travellers }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.coverage' | transloco }}</span>
            <span class="font-medium">
              @if (wizardService.isSingleTraveler()) {
                {{ 'contribute.coverage.singleTraveler' | transloco }}
              } @else {
                {{ 'contribute.coverage.fullTrip' | transloco }}
              }
            </span>
          </div>
        </div>
      </div>

      <!-- Project Section -->
      <div class="border-b border-greyscale-200 p-4">
        <h3 class="mb-3 font-semibold text-gray-900">{{ 'contribute.reviewConfirm.projectSummary' | transloco }}</h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.project' | transloco }}</span>
            <span class="font-medium text-right">{{ getProjectName() }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.pricePerTonne' | transloco }}</span>
            <span class="font-medium">
              {{ wizardService.selectedProject()?.pricePerTonne | number:'1.2-2' }} EUR/t
            </span>
          </div>
        </div>
      </div>

      <!-- Contribution Section -->
      <div class="border-b border-greyscale-200 p-4">
        <h3 class="mb-3 font-semibold text-gray-900">{{ 'contribute.reviewConfirm.contributionSummary' | transloco }}</h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.percentage' | transloco }}</span>
            <span class="font-medium">{{ wizardService.globalPercentage() }}%</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.co2ToCompensate' | transloco }}</span>
            <span class="font-medium">
              {{ wizardService.co2ToCompensate() | number:'1.0-0' }} kg
              ({{ wizardService.co2ToCompensateTonnes() | number:'1.2-2' }} t)
            </span>
          </div>
        </div>
      </div>

      <!-- Certificate Section -->
      <div class="p-4">
        <h3 class="mb-3 font-semibold text-gray-900">{{ 'contribute.reviewConfirm.certificateSummary' | transloco }}</h3>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.reviewConfirm.recipientName' | transloco }}</span>
            <span class="font-medium text-right">{{ wizardService.certificateRecipientName() }}</span>
          </div>
          @if (wizardService.certificateMessage()) {
            <div>
              <span class="text-gray-600">{{ 'contribute.reviewConfirm.message' | transloco }}</span>
              <p class="mt-1 italic text-gray-700">"{{ wizardService.certificateMessage() }}"</p>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Right Column: Payment Panel (Sticky) -->
    <div class="lg:sticky lg:top-4 lg:self-start">
      <div class="overflow-hidden rounded-lg border border-greyscale-200">
        <!-- Pricing Section -->
        <div class="bg-primary-100 p-4">
          <h3 class="font-semibold text-primary-700">{{ 'contribute.reviewConfirm.pricingSummary' | transloco }}</h3>
        </div>
        <div class="space-y-3 bg-greyscale-50 p-4">
          <div class="flex justify-between">
            <span class="text-gray-600">{{ 'contribute.pricing.subtotal' | transloco }}</span>
            <span class="font-medium">{{ wizardService.contributionAmount() | number:'1.2-2' }} EUR</span>
          </div>
          <div>
            <div class="flex justify-between">
              <span class="text-gray-600">
                @if (wizardService.isReverseCharge()) {
                  {{ 'contribute.pricing.vatReverseCharge' | transloco }}
                } @else {
                  {{ 'contribute.pricing.vat' | transloco }} ({{ (wizardService.vatRate() * 100) | number:'1.0-0' }}%)
                }
              </span>
              <span class="font-medium">{{ wizardService.vatAmount() | number:'1.2-2' }} EUR</span>
            </div>
            @if (wizardService.isReverseCharge()) {
              <p class="mt-1 text-xs text-blue-600">
                {{ 'contribute.pricing.reverseChargeNote' | transloco }}
              </p>
            }
          </div>
          <div class="border-t border-greyscale-200 pt-3">
            <div class="flex justify-between">
              <span class="text-lg font-bold text-gray-900">{{ 'contribute.pricing.total' | transloco }}</span>
              <span class="text-xl font-bold text-primary-600">
                {{ wizardService.totalAmount() | number:'1.2-2' }} EUR
              </span>
            </div>
          </div>
        </div>

        <!-- Payment Method Selection -->
        <div class="border-t border-greyscale-200 p-4">
          <h4 class="mb-3 font-semibold text-gray-900">
            {{ 'contribute.payment.chooseMethod' | transloco }}
          </h4>
          <div class="space-y-3">
            <!-- Card Payment Option -->
            <div
              class="cursor-pointer rounded-lg border-2 p-3 transition-all hover:border-primary-300"
              [class.border-primary-500]="wizardService.selectedPaymentMethod() === 'CARD'"
              [class.bg-primary-50]="wizardService.selectedPaymentMethod() === 'CARD'"
              [class.border-greyscale-200]="wizardService.selectedPaymentMethod() !== 'CARD'"
              (click)="selectPaymentMethod('CARD')"
              (keydown.enter)="selectPaymentMethod('CARD')"
              (keydown.space)="selectPaymentMethod('CARD'); $event.preventDefault()"
              tabindex="0"
              role="radio"
              [attr.aria-checked]="wizardService.selectedPaymentMethod() === 'CARD'"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex h-9 w-9 items-center justify-center rounded-full"
                  [class.bg-primary-100]="wizardService.selectedPaymentMethod() === 'CARD'"
                  [class.bg-greyscale-100]="wizardService.selectedPaymentMethod() !== 'CARD'"
                >
                  <i
                    class="pi pi-credit-card text-lg"
                    [class.text-primary-600]="wizardService.selectedPaymentMethod() === 'CARD'"
                    [class.text-greyscale-600]="wizardService.selectedPaymentMethod() !== 'CARD'"
                  ></i>
                </div>
                <div>
                  <p class="font-semibold text-gray-900">
                    {{ 'contribute.payment.card' | transloco }}
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ 'contribute.payment.cardDescription' | transloco }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Bank Transfer Option -->
            <div
              class="cursor-pointer rounded-lg border-2 p-3 transition-all hover:border-primary-300"
              [class.border-primary-500]="wizardService.selectedPaymentMethod() === 'BANK_TRANSFER'"
              [class.bg-primary-50]="wizardService.selectedPaymentMethod() === 'BANK_TRANSFER'"
              [class.border-greyscale-200]="wizardService.selectedPaymentMethod() !== 'BANK_TRANSFER'"
              (click)="selectPaymentMethod('BANK_TRANSFER')"
              (keydown.enter)="selectPaymentMethod('BANK_TRANSFER')"
              (keydown.space)="selectPaymentMethod('BANK_TRANSFER'); $event.preventDefault()"
              tabindex="0"
              role="radio"
              [attr.aria-checked]="wizardService.selectedPaymentMethod() === 'BANK_TRANSFER'"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex h-9 w-9 items-center justify-center rounded-full"
                  [class.bg-primary-100]="wizardService.selectedPaymentMethod() === 'BANK_TRANSFER'"
                  [class.bg-greyscale-100]="wizardService.selectedPaymentMethod() !== 'BANK_TRANSFER'"
                >
                  <i
                    class="pi pi-building text-lg"
                    [class.text-primary-600]="wizardService.selectedPaymentMethod() === 'BANK_TRANSFER'"
                    [class.text-greyscale-600]="wizardService.selectedPaymentMethod() !== 'BANK_TRANSFER'"
                  ></i>
                </div>
                <div>
                  <p class="font-semibold text-gray-900">
                    {{ 'contribute.payment.bankTransfer' | transloco }}
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ 'contribute.payment.bankTransferDescription' | transloco }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button
            pButton
            class="mt-4 w-full"
            [icon]="wizardService.selectedPaymentMethod() === 'CARD' ? 'pi pi-credit-card' : 'pi pi-check'"
            iconPos="right"
            [loading]="submitting"
            [disabled]="!canSubmit() || submitting"
            (click)="onSubmit()"
          >
            {{ submitButtonLabel() | transloco }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Navigation (Back button only) -->
  <div class="flex justify-start pt-4">
    <button
      pButton
      icon="pi pi-arrow-left"
      severity="secondary"
      [disabled]="submitting"
      (click)="onBack()"
    >
      {{ 'common.back' | transloco }}
    </button>
  </div>
</div>
